import { useAppSelector } from "@app/store";
import Edit from "@assets/icons/edit.svg";
import { Block } from "@components/block";
import { Button } from "@components/button";
import { Device } from "@components/device";
import { errorMessage } from "@components/forms/error";
import { GlobalLoader } from "@components/loader";
import { RemoveDeviceConfirm } from "@components/remove-device-confirm";
import { StopDeviceConfirm } from "@components/stop-device-confirm";
import { useDevicesDispatch } from "@features/device";
import { DeviceType } from "@features/device/slice";
import { formatDate } from "@helpers/date";
import { yupResolver } from "@hookform/resolvers/yup";
import { useBoolState } from "@hooks/use-bool-state";
import classNames from "classnames";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { DeviceInfoRow } from "./device-info-row";
import styles from "./device-info.module.scss";

type Props = {
  device: DeviceType;
  className?: string;
};

const schema = yup.object().shape({
  name: yup.string().required(errorMessage.required).trim(),
});

type FormData = { name: string };

export const DeviceDetailInfo = ({ device, className }: Props) => {
  const isDeleted = device.deleted;
  const isActive = device.active;
  const isWorking = device.at_work;

  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.pathname.endsWith("/edit") && !isDeleted;

  const [isRemoveModalOpen, showRemoveModal, hideRemoveModal] =
    useBoolState(false);
  const [isStopModalOpen, showStopModal, hideStopModal] = useBoolState(false);

  const createdAt = useMemo(
    () =>
      device.created_at_unix != null &&
      formatDate(new Date(device.created_at_unix * 1000), "dd/MM/yyyy kk:mm"),
    [device.created_at_unix]
  );

  const lastActive = useMemo(
    () =>
      device.last_active_unix != null &&
      formatDate(new Date(device.last_active_unix * 1000), "dd/MM/yyyy kk:mm"),
    [device.last_active_unix]
  );

  const { control, handleSubmit, reset } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { name: device.name },
  });

  useEffect(() => {
    if (!isEdit) {
      reset();
    }
  }, [isEdit, reset]);

  const isUpdating = useAppSelector((state) => state.device.isUpdating);
  const { updateDevice } = useDevicesDispatch();

  const onEndEdit = useCallback(() => navigate(-1), [navigate]);

  const onCloseRemoveModal = useCallback(() => {
    hideRemoveModal();
    navigate(`${location.pathname}/../`);
  }, [hideRemoveModal]);

  const onSaveDevice = useCallback(
    async (data: FormData) => {
      await updateDevice({ deviceId: device.id, name: data.name });
      onEndEdit();
    },
    [device.id, onEndEdit, updateDevice]
  );

  const startDevice = useCallback(() => {
    updateDevice({ deviceId: device.id, at_work: true });
  }, [device.id, updateDevice]);

  return (
    <>
      <Block key={device.id} className={classNames(styles.device, className)}>
        <div className={styles.headerDevice}>
          <h3 className={styles.headerDeviceTitle}>Данные об устройстве</h3>
          {!isDeleted && (
            <div className={styles.headerDeviceButtons}>
              {isEdit ? (
                <Button type="outline" onClick={showRemoveModal}>
                  Удалить устройство
                </Button>
              ) : (
                <>
                  <Button
                    className={styles.editButton}
                    iconClassName={styles.editButtonIcon}
                    icon={Edit}
                    onClick={() => navigate("edit")}
                  >
                    Редактировать
                  </Button>
                  {isWorking ? (
                    <Button
                      type="outline"
                      className={styles.stopButton}
                      onClick={showStopModal}
                    >
                      Остановить устройство
                    </Button>
                  ) : (
                    <Button
                      type="outline"
                      className={styles.stopButton}
                      onClick={startDevice}
                      disabled={!isActive}
                    >
                      Запустить устройство
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className={styles.deviceDetails}>
          <Device
            charge={device.charge}
            name={device.name}
            active={isActive}
            at_work={isWorking}
            deleted={isDeleted}
          />
          <div className={styles.deviceDetailsInfoWrapper}>
            <div className={styles.deviceDetailsInfo}>
              <DeviceInfoRow
                control={isEdit ? control : undefined}
                label="Имя устройства"
                value={device.name}
              />
              <DeviceInfoRow label="Модель" value={device.device_model} />
              <DeviceInfoRow label="ID устройства" value={device.fingerprint} />
              <DeviceInfoRow
                label="Номер сим карты"
                value={device.phone_number}
              />
              {lastActive && (
                <DeviceInfoRow
                  label="Последняя активность"
                  value={lastActive}
                />
              )}
              <DeviceInfoRow label="Версия APK" value={device.app_version} />
              {createdAt && (
                <DeviceInfoRow label="Дата добавления" value={createdAt} />
              )}
            </div>
            {isEdit && (
              <div className={styles.buttons}>
                <Button type="outline" onClick={onEndEdit}>
                  Отменить изменения
                </Button>
                <Button onClick={handleSubmit(onSaveDevice)}>
                  Сохранить изменения
                </Button>
              </div>
            )}
          </div>
        </div>
      </Block>
      {isUpdating && <GlobalLoader />}
      <RemoveDeviceConfirm
        hide={onCloseRemoveModal}
        isOpen={isRemoveModalOpen}
        deviceId={device.id}
      />
      <StopDeviceConfirm
        hide={hideStopModal}
        isOpen={isStopModalOpen}
        deviceId={device.id}
      />
    </>
  );
};
